import React, { useState, useMemo } from 'react';
import { Search, X, Eye, Package, Tag, Building2, Plus, ChevronRight, ClipboardCheck } from 'lucide-react';
import { UploadCloud } from '../components/Icons';
import { baseData } from '../data/mockData';
import MultiSelectBase from '../components/MultiSelectBase';

// 统计卡片组件
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);

const MaterialsView = ({ materials, onAddMaterial, onUpdateMaterial, onDeleteMaterial }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBase, setFilterBase] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // 计算统计数据
  const stats = useMemo(() => {
    const categories = new Set(materials.map(m => m.category));

    return {
      total: materials.length,
      categories: categories.size,
      bases: baseData.length,
    };
  }, [materials]);

  // 提取分类选项
  const categoryOptions = useMemo(() => {
    const categories = new Set(materials.map(m => m.category).filter(Boolean));
    return Array.from(categories).sort();
  }, [materials]);

  // 是否有活跃筛选
  const hasActiveFilters = searchQuery || filterBase || filterCategory;

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterBase('');
    setFilterCategory('');
  };

  // 筛选物料
  const filteredMaterials = useMemo(() => {
    let result = [...materials];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m =>
        m.name.toLowerCase().includes(query) ||
        m.standard?.toLowerCase().includes(query)
      );
    }

    // 基地筛选
    if (filterBase) {
      result = result.filter(m => m.baseIds?.includes(filterBase));
    }

    // 分类筛选
    if (filterCategory) {
      result = result.filter(m => m.category === filterCategory);
    }

    return result;
  }, [materials, searchQuery, filterBase, filterCategory]);

  // 详情弹窗
  const MaterialDetailModal = ({ material, onClose }) => {
    if (!material) return null;
    const linkedBases = baseData.filter(b => material.baseIds?.includes(b.id));

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[600px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                {material.name}
                <span className="bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded text-xs font-normal">
                  {material.category}
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-1 font-mono">编码: {material.id}</p>
            </div>
            <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-600" /></button>
          </div>
          <div className="p-6 space-y-6 overflow-y-auto">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><ClipboardCheck size={16} className="text-blue-600"/> 验收标准</h4>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                {material.standard}
                <br/><br/>
                <span className="text-xs text-slate-400">* 注：所有杂质扣重标准以此为基准，特殊情况需由质检主管审批。</span>
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><Building2 size={16} className="text-indigo-600"/> 关联收货基地</h4>
              <div className="space-y-2">
                {linkedBases.length > 0 ? linkedBases.map(b => (
                  <div key={b.id} className="flex justify-between items-center p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs">
                        {b.name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{b.name}</div>
                        <div className="text-xs text-slate-500">{b.manager}</div>
                      </div>
                    </div>
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">收货中</span>
                  </div>
                )) : <div className="text-sm text-slate-400 p-4 text-center">暂无关联基地</div>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border border-slate-200 rounded-lg">
                <span className="block text-xs text-slate-400 mb-1">计量单位</span>
                <span className="font-bold text-slate-700">{material.unit}</span>
              </div>
              <div className="p-3 border border-slate-200 rounded-lg">
                <span className="block text-xs text-slate-400 mb-1">最近更新</span>
                <span className="font-mono text-slate-600 text-sm">{material.updatedAt || '-'}</span>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 bg-white flex justify-end">
            <button onClick={onClose} className="px-6 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200">关闭</button>
          </div>
        </div>
      </div>
    );
  };

  // 新增/编辑弹窗
  const AddMaterialModal = ({ onClose, material }) => {
    const isEdit = !!material;
    const [name, setName] = useState(material?.name || '');
    const [unit, setUnit] = useState(material?.unit || '吨');
    const [standard, setStandard] = useState(material?.standard || '');
    const [selectedBases, setSelectedBases] = useState(material?.baseIds || []);

    const handleSubmit = () => {
      if (!name || !unit || !standard || selectedBases.length === 0) {
        alert('请填写所有必填项');
        return;
      }

      const materialData = {
        id: isEdit ? material.id : `M${Date.now()}`,
        name,
        unit,
        standard,
        baseIds: selectedBases,
        category: material?.category || '重废',
        img: material?.img || 'placeholder'
      };

      if (isEdit) {
        onUpdateMaterial(materialData);
      } else {
        onAddMaterial(materialData);
      }
      alert(isEdit ? '编辑物料成功' : '新增物料成功');
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[600px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
            <h3 className="text-lg font-bold text-slate-800">{isEdit ? '编辑物料' : '新增物料'}</h3>
            <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-600" /></button>
          </div>
          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">物料名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="例如：优质重废"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">计量单位 <span className="text-red-500">*</span></label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="吨">吨</option>
                  <option value="千克">千克</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">验收标准 <span className="text-red-500">*</span></label>
              <textarea
                value={standard}
                onChange={(e) => setStandard(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm h-20 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="请输入详细的验收标准和扣杂规则..."
              ></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">关联基地 (可多选) <span className="text-red-500">*</span></label>
              <MultiSelectBase selectedIds={selectedBases} onChange={setSelectedBases} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">物料图片 (支持多张)</label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 cursor-pointer transition-colors">
                <UploadCloud size={32} className="mb-2" />
                <span className="text-sm">点击或拖拽上传图片</span>
                <span className="text-xs mt-1">支持 JPG, PNG 格式</span>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg">取消</button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">保存物料</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">基地物料管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有物料类型和验收标准</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm"
        >
          <Plus size={16} /> 新增物料
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          title="全部物料"
          value={stats.total}
          icon={Package}
          iconColor="text-slate-600"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="物料分类"
          value={stats.categories}
          icon={Tag}
          iconColor="text-purple-600"
          bgColor="bg-purple-50"
        />
        <StatCard
          title="关联基地"
          value={stats.bases}
          icon={Building2}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
        />
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索物料名称、验收标准..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 基地筛选 */}
            <select
              value={filterBase}
              onChange={(e) => setFilterBase(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部基地</option>
              {baseData.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>

            {/* 分类筛选 */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部分类</option>
              {categoryOptions.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 表格区域 */}
        {filteredMaterials.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">暂无物料数据</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">物料名称</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">分类</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">验收标准</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">单位</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">关联基地</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">创建时间</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMaterials.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-3">
                      <span className="font-medium text-slate-800">{m.name}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-600">
                        {m.category || '-'}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600 max-w-[200px] truncate block" title={m.standard}>
                        {m.standard}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{m.unit}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-500">{m.baseIds?.length || 0} 个基地</span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-500">{m.createdAt}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedMaterial(m)}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye size={14} />
                          详情
                          <ChevronRight size={14} />
                        </button>
                        <button
                          onClick={() => setEditingMaterial(m)}
                          className="text-sm text-slate-500 hover:text-slate-700"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(m)}
                          className="text-sm text-red-500 hover:text-red-600"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部统计 */}
        {filteredMaterials.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredMaterials.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{materials.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 弹窗 */}
      {showAddModal && <AddMaterialModal onClose={() => setShowAddModal(false)} />}
      {editingMaterial && <AddMaterialModal onClose={() => setEditingMaterial(null)} material={editingMaterial} />}
      {selectedMaterial && <MaterialDetailModal material={selectedMaterial} onClose={() => setSelectedMaterial(null)} />}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
          <div className="bg-white rounded-xl shadow-xl w-[400px] p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4">确认删除</h3>
            <p className="text-sm text-slate-600 mb-6">确定要删除物料"{showDeleteConfirm.name}"吗？此操作不可恢复。</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg">取消</button>
              <button onClick={() => { onDeleteMaterial(showDeleteConfirm.id); setShowDeleteConfirm(null); }} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsView;
